import { parse, type MathNode } from 'mathjs';
import { utils as xlsxUtils, write as xlsxWrite, type CellObject, type WorkBook, type WorkSheet } from 'xlsx';
import type {
    DistributionConfig,
    DistributionNodeData,
    SimulationGraph,
    SimulationNode,
    SimulationResultPayload
} from '../models/simulation';
import { buildNameToIdMap, extractVariableNames, replaceVariableReferences } from './expressionParser';
import { topologicalSort } from './graph';

/**
 * Exported simulation format
 */
export interface ExportedSimulation {
    version: '1.0';
    exportedAt: string;
    simulation: {
        name: string;
        description?: string;
        tags?: string[];
        graph: SimulationGraph;
    };
}

/**
 * Export simulation to JSON string
 */
export const exportSimulationToJSON = (
    name: string,
    graph: SimulationGraph,
    description?: string,
    tags?: string[]
): string => {
    const exported: ExportedSimulation = {
        version: '1.0',
        exportedAt: new Date().toISOString(),
        simulation: {
            name,
            description,
            tags,
            graph
        }
    };

    return JSON.stringify(exported, null, 2);
};

/**
 * Import simulation from JSON string with validation
 */
export const importSimulationFromJSON = (
    jsonString: string
): { valid: true; data: ExportedSimulation } | { valid: false; error: string } => {
    try {
        const parsed = JSON.parse(jsonString);

        // Validate structure
        if (!parsed.version) {
            return { valid: false, error: 'Invalid simulation file format: missing version' };
        }

        if (parsed.version !== '1.0') {
            return { valid: false, error: `Unsupported file version: ${parsed.version}` };
        }

        if (!parsed.simulation) {
            return { valid: false, error: 'Invalid simulation file format: missing simulation data' };
        }

        if (!parsed.simulation.graph) {
            return { valid: false, error: 'Invalid simulation file format: missing graph' };
        }

        // Validate graph structure
        const graph = parsed.simulation.graph;

        if (!graph.nodes || !Array.isArray(graph.nodes)) {
            return { valid: false, error: 'Invalid graph structure: missing or invalid nodes array' };
        }

        if (!graph.edges || !Array.isArray(graph.edges)) {
            return { valid: false, error: 'Invalid graph structure: missing or invalid edges array' };
        }

        // Validate node structure
        for (const node of graph.nodes) {
            if (!node.id || typeof node.id !== 'string') {
                return { valid: false, error: 'Invalid node: missing or invalid id' };
            }

            if (!node.data || typeof node.data !== 'object') {
                return { valid: false, error: `Invalid node ${node.id}: missing or invalid data` };
            }

            if (!node.data.type || !['distribution', 'formula'].includes(node.data.type)) {
                return { valid: false, error: `Invalid node ${node.id}: invalid type` };
            }

            if (!node.data.name || typeof node.data.name !== 'string') {
                return { valid: false, error: `Invalid node ${node.id}: missing or invalid name` };
            }
        }

        // Validate edge structure
        for (const edge of graph.edges) {
            if (!edge.id || typeof edge.id !== 'string') {
                return { valid: false, error: 'Invalid edge: missing or invalid id' };
            }

            if (!edge.source || typeof edge.source !== 'string') {
                return { valid: false, error: `Invalid edge ${edge.id}: missing or invalid source` };
            }

            if (!edge.target || typeof edge.target !== 'string') {
                return { valid: false, error: `Invalid edge ${edge.id}: missing or invalid target` };
            }
        }

        return { valid: true, data: parsed as ExportedSimulation };
    } catch (error) {
        if (error instanceof SyntaxError) {
            return { valid: false, error: 'Failed to parse JSON file: invalid JSON syntax' };
        }
        return { valid: false, error: 'Failed to parse JSON file: unknown error' };
    }
};

/**
 * Export simulation results to CSV format
 */
export const exportResultsToCSV = (
    results: SimulationResultPayload,
    graph: SimulationGraph
): string => {
    const headers = ['Node ID', 'Node Name', 'Node Type', 'Unit', 'Mean', 'Median', 'P10', 'P90', 'Std Dev'];
    const rows = [headers.join(',')];

    Object.entries(results.results).forEach(([nodeId, result]) => {
        const node = graph.nodes.find(n => n.id === nodeId);
        if (!node) return;

        // Escape node name for CSV (handle commas and quotes)
        const escapedName = `"${node.data.name.replace(/"/g, '""')}"`;

        const unit = result.unit ?? (node.data.type === 'distribution' ? node.data.unit : undefined);
        const unitCell = unit ? `"${unit.replace(/"/g, '""')}"` : '';

        const row = [
            nodeId,
            escapedName,
            node.data.type,
            unitCell,
            result.summary.mean.toFixed(4),
            result.summary.median.toFixed(4),
            result.summary.p10.toFixed(4),
            result.summary.p90.toFixed(4),
            result.summary.std.toFixed(4)
        ];

        rows.push(row.join(','));
    });

    return rows.join('\n');
};

export interface ExportResultsToExcelOptions {
    simulationName?: string;
    scenarioName?: string;
    includeOnlyOutputs?: boolean;
    // Backward-compatibility options from legacy SpreadsheetML exporter.
    includeGraphStructure?: boolean;
    includeRawSamples?: boolean;
}

const sanitizeWorksheetName = (name: string): string =>
    name.replace(/[\\/*?:\[\]]/g, '_').slice(0, 31) || 'Sheet';

const isDistributionNode = (
    node: SimulationNode
): node is SimulationNode & { data: DistributionNodeData } => node.data.type === 'distribution';

const toCellRef = (columnIndex: number, rowIndex: number, absolute = true): string => {
    let col = columnIndex;
    let colLabel = '';
    while (col > 0) {
        const rem = (col - 1) % 26;
        colLabel = String.fromCharCode(65 + rem) + colLabel;
        col = Math.floor((col - 1) / 26);
    }

    return absolute ? `$${colLabel}$${rowIndex}` : `${colLabel}${rowIndex}`;
};

const excelFunctionMap: Record<string, string> = {
    abs: 'ABS',
    acos: 'ACOS',
    asin: 'ASIN',
    atan: 'ATAN',
    atan2: 'ATAN2',
    ceil: 'CEILING',
    ceiling: 'CEILING',
    cos: 'COS',
    exp: 'EXP',
    floor: 'FLOOR',
    if: 'IF',
    ln: 'LN',
    log10: 'LOG10',
    max: 'MAX',
    mean: 'AVERAGE',
    median: 'MEDIAN',
    min: 'MIN',
    mod: 'MOD',
    pow: 'POWER',
    round: 'ROUND',
    sin: 'SIN',
    sqrt: 'SQRT',
    std: 'STDEV.S',
    stddev: 'STDEV.S',
    stddevsamp: 'STDEV.S',
    sum: 'SUM',
    tan: 'TAN',
    trunc: 'TRUNC'
};

const getAccessorPropertyName = (node: any): string | null => {
    if (!node || node.type !== 'AccessorNode') {
        return null;
    }

    const objectName = node.object?.name;
    if (objectName !== 'math' && objectName !== 'Math') {
        return null;
    }

    const indexNode = node.index?.dimensions?.[0];
    if (!indexNode || indexNode.type !== 'ConstantNode') {
        return null;
    }

    return String(indexNode.value);
};

const resolveFunctionName = (fnNode: any): string | null => {
    if (!fnNode) {
        return null;
    }

    if (fnNode.type === 'SymbolNode') {
        return String(fnNode.name);
    }

    return getAccessorPropertyName(fnNode);
};

const asNumericLiteral = (value: unknown): string | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value);
    }

    if (typeof value === 'boolean') {
        return value ? 'TRUE' : 'FALSE';
    }

    if (typeof value === 'string') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return String(parsed);
        }

        return `"${value.replace(/"/g, '""')}"`;
    }

    return null;
};

const mathNodeToExcelExpression = (
    node: MathNode,
    identifierCellRefs: Map<string, string>
): string | null => {
    const typedNode = node as any;

    switch (typedNode.type) {
        case 'ParenthesisNode': {
            const inner = mathNodeToExcelExpression(typedNode.content, identifierCellRefs);
            return inner ? `(${inner})` : null;
        }
        case 'ConstantNode':
            return asNumericLiteral(typedNode.value);
        case 'SymbolNode': {
            const mappedRef = identifierCellRefs.get(typedNode.name);
            if (mappedRef) {
                return mappedRef;
            }

            if (typedNode.name === 'pi') {
                return 'PI()';
            }
            if (typedNode.name === 'e') {
                return 'EXP(1)';
            }

            return String(typedNode.name);
        }
        case 'AccessorNode': {
            const propName = getAccessorPropertyName(typedNode);
            if (!propName) {
                return null;
            }

            if (propName === 'pi') {
                return 'PI()';
            }
            if (propName === 'e') {
                return 'EXP(1)';
            }

            return null;
        }
        case 'OperatorNode': {
            if (typedNode.fn === 'unaryMinus' && typedNode.args?.length === 1) {
                const arg = mathNodeToExcelExpression(typedNode.args[0], identifierCellRefs);
                return arg ? `(-${arg})` : null;
            }
            if (typedNode.fn === 'unaryPlus' && typedNode.args?.length === 1) {
                const arg = mathNodeToExcelExpression(typedNode.args[0], identifierCellRefs);
                return arg ? `(+${arg})` : null;
            }

            if (!typedNode.args || typedNode.args.length !== 2) {
                return null;
            }

            const left = mathNodeToExcelExpression(typedNode.args[0], identifierCellRefs);
            const right = mathNodeToExcelExpression(typedNode.args[1], identifierCellRefs);
            if (!left || !right) {
                return null;
            }

            if (typedNode.op === '%' || typedNode.fn === 'mod') {
                return `MOD(${left},${right})`;
            }

            if (['+', '-', '*', '/', '^'].includes(typedNode.op)) {
                return `(${left}${typedNode.op}${right})`;
            }

            return null;
        }
        case 'FunctionNode': {
            const functionName = resolveFunctionName(typedNode.fn);
            if (!functionName) {
                return null;
            }

            const args: string[] = [];
            for (const argNode of typedNode.args ?? []) {
                const arg = mathNodeToExcelExpression(argNode, identifierCellRefs);
                if (!arg) {
                    return null;
                }
                args.push(arg);
            }

            const normalizedName = functionName.toLowerCase();
            if (normalizedName === 'log') {
                if (args.length === 1) {
                    return `LN(${args[0]})`;
                }
                if (args.length === 2) {
                    return `LOG(${args[0]},${args[1]})`;
                }
            }

            if (normalizedName === 'round' && args.length === 1) {
                return `ROUND(${args[0]},0)`;
            }

            if ((normalizedName === 'ceil' || normalizedName === 'ceiling') && args.length === 1) {
                return `CEILING(${args[0]},1)`;
            }

            const excelFunction = excelFunctionMap[normalizedName] ?? functionName.toUpperCase();
            return `${excelFunction}(${args.join(',')})`;
        }
        default:
            return null;
    }
};

const toExcelFormulaFromExpression = (
    expression: string,
    nameToIdMap: Map<string, string>,
    nodeMeanCellRefMap: Map<string, string>
): string | null => {
    const variableNames = extractVariableNames(expression);
    const { expression: sanitizedExpression, identifierMap } = replaceVariableReferences(expression, variableNames);
    const identifierCellRefs = new Map<string, string>();

    for (const [identifier, nodeName] of identifierMap) {
        const nodeId = nameToIdMap.get(nodeName);
        const ref = nodeId ? nodeMeanCellRefMap.get(nodeId) : undefined;
        if (!ref) {
            return null;
        }

        identifierCellRefs.set(identifier, ref);
    }

    try {
        const parsed = parse(sanitizedExpression);
        return mathNodeToExcelExpression(parsed, identifierCellRefs);
    } catch {
        return null;
    }
};

const getDistributionParams = (config: DistributionConfig): {
    fixedValue?: number;
    normalMean?: number;
    normalStdDev?: number;
    min?: number;
    mode?: number;
    max?: number;
} => {
    switch (config.kind) {
        case 'fixed':
            return { fixedValue: config.value };
        case 'normal':
            return {
                normalMean: config.mean,
                normalStdDev: config.std,
                min: config.min,
                max: config.max
            };
        case 'triangular':
            return {
                min: config.min,
                mode: config.mode,
                max: config.max
            };
        case 'uniform':
            return {
                min: config.min,
                max: config.max
            };
        default:
            return {};
    }
};

const normalizeSheetCellValue = (value: unknown): string | number | boolean | null => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string' || typeof value === 'boolean') {
        return value;
    }

    return null;
};

const setFormulaCell = (
    sheet: WorkSheet,
    address: string,
    formula: string,
    value?: number | null
): void => {
    const cell: CellObject = { t: 'n', f: formula };
    if (typeof value === 'number' && Number.isFinite(value)) {
        cell.v = value;
    }
    sheet[address] = cell;
};

const buildDistributionMeanFormula = (
    distributionKind: DistributionConfig['kind'],
    rowIndex: number
): string | null => {
    const fixedRef = toCellRef(7, rowIndex, true); // G: Fixed Value
    const normalMeanRef = toCellRef(8, rowIndex, true); // H: Normal Mean
    const minRef = toCellRef(10, rowIndex, true); // J: Min
    const modeRef = toCellRef(11, rowIndex, true); // K: Mode
    const maxRef = toCellRef(12, rowIndex, true); // L: Max

    switch (distributionKind) {
        case 'fixed':
            return fixedRef;
        case 'normal':
            return normalMeanRef;
        case 'uniform':
            return `((${minRef}+${maxRef})/2)`;
        case 'triangular':
            return `((${minRef}+${modeRef}+${maxRef})/3)`;
        default:
            return null;
    }
};

/**
 * Export simulation results to a true `.xlsx` workbook.
 * The workbook contains:
 * - Model: one sheet containing all nodes
 *   - Distribution rows at the top
 *   - Formula rows below with working Excel formulas
 */
export const exportResultsToExcelWorkbook = (
    results: SimulationResultPayload,
    graph: SimulationGraph,
    options: ExportResultsToExcelOptions = {}
): ArrayBuffer => {
    const {
        simulationName = graph.name,
        scenarioName,
        includeOnlyOutputs = false
    } = options;

    const nameToIdMap = buildNameToIdMap(graph.nodes);
    const sortedNodes = topologicalSort(graph.nodes, graph.edges);
    const filteredNodes = includeOnlyOutputs
        ? sortedNodes.filter((node) => node.data.isOutput)
        : sortedNodes;

    const distributionNodes = filteredNodes.filter(isDistributionNode);
    const formulaNodes = filteredNodes.filter((node) => node.data.type === 'formula');
    const orderedNodes = [...distributionNodes, ...formulaNodes];

    const modelHeader = [
        'Section',
        'Order',
        'Node ID',
        'Node Name',
        'Node Type',
        'Distribution Kind',
        'Fixed Value',
        'Normal Mean',
        'Normal Std Dev',
        'Min',
        'Mode',
        'Max',
        'Unit',
        'Expression',
        'Mean'
    ];

    const meanCellRefByNodeId = new Map<string, string>();
    orderedNodes.forEach((node, index) => {
        const rowIndex = index + 2; // 1-based + header row
        meanCellRefByNodeId.set(node.id, toCellRef(15, rowIndex, true)); // O: Mean
    });

    const modelRows = orderedNodes.map((node, index) => {
        const result = results.results[node.id];
        const mean = result?.summary.mean;
        const distributionParams = node.data.type === 'distribution' ? getDistributionParams(node.data.config) : {};
        const unit = result?.unit ?? (node.data.type === 'distribution' ? node.data.unit ?? '' : '');

        return [
            node.data.type === 'distribution' ? 'Distribution' : 'Calculation',
            index + 1,
            node.id,
            node.data.name,
            node.data.type,
            node.data.type === 'distribution' ? node.data.config.kind : '',
            distributionParams.fixedValue ?? null,
            distributionParams.normalMean ?? null,
            distributionParams.normalStdDev ?? null,
            distributionParams.min ?? null,
            distributionParams.mode ?? null,
            distributionParams.max ?? null,
            unit,
            node.data.type === 'formula' ? node.data.expression : '',
            typeof mean === 'number' && Number.isFinite(mean) ? Number(mean.toFixed(8)) : null
        ].map(normalizeSheetCellValue);
    });

    const modelSheet = xlsxUtils.aoa_to_sheet([modelHeader, ...modelRows]);

    orderedNodes.forEach((node, index) => {
        const rowIndex = index + 2;
        const cellAddress = toCellRef(15, rowIndex, false); // O: Mean
        const resultMean = results.results[node.id]?.summary.mean;
        const cachedValue = typeof resultMean === 'number' && Number.isFinite(resultMean)
            ? Number(resultMean.toFixed(8))
            : undefined;

        if (node.data.type === 'distribution') {
            const distributionFormula = buildDistributionMeanFormula(node.data.config.kind, rowIndex);
            if (distributionFormula) {
                setFormulaCell(modelSheet, cellAddress, distributionFormula, cachedValue);
            }
            return;
        }

        const formula = toExcelFormulaFromExpression(
            node.data.expression,
            nameToIdMap,
            meanCellRefByNodeId
        );

        if (formula) {
            setFormulaCell(modelSheet, cellAddress, formula, cachedValue);
            return;
        }

        if (typeof cachedValue === 'number') {
            setFormulaCell(modelSheet, cellAddress, String(cachedValue), cachedValue);
        }
    });

    const workbook: WorkBook = xlsxUtils.book_new();
    workbook.Props = {
        Title: `${simulationName} means export`,
        Subject: scenarioName ? `Scenario: ${scenarioName}` : 'Baseline Scenario',
        Author: 'Simcarlo',
        CreatedDate: new Date()
    };

    // Force calculation refresh when opened.
    (workbook as any).Workbook = { CalcPr: { fullCalcOnLoad: '1' } };

    xlsxUtils.book_append_sheet(workbook, modelSheet, sanitizeWorksheetName('Model'));

    return xlsxWrite(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
};

/**
 * Download a file to the user's computer
 */
export const downloadFile = (content: BlobPart, filename: string, mimeType: string): void => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

/**
 * Create a safe filename from a simulation name
 */
export const createSafeFilename = (name: string, extension: string): string => {
    const safeName = name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const timestamp = Date.now();
    return `${safeName}_${timestamp}.${extension}`;
};
